import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import axios from 'axios'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

import './styles.css'
import reportWebVitals from './reportWebVitals.ts'
import { AuthProvider, getTokenAsync, useAuth } from './auth.tsx'

import * as Sentry from "@sentry/react";
import { Analytics } from "@vercel/analytics/react"


Sentry.init({
  dsn: "https://c7f72ca905deb10f149918a962a7c712@o153477.ingest.us.sentry.io/4509404337602560",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
});

export const queryClient = new QueryClient()

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,

})

axios.defaults.baseURL = import.meta.env.VITE_API_URL

// Store the auth context reference for interceptors
let authContextRef: any = null

// Shared promise to prevent concurrent refresh attempts
let refreshTokenPromise: Promise<boolean> | null = null

// Request interceptor to add token
axios.interceptors.request.use(async (config) => {
  const token = await getTokenAsync();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (authContextRef?.refreshToken) {
        try {
          // If there's already a refresh in progress, wait for it
          if (refreshTokenPromise) {
            await refreshTokenPromise;
          } else {
            // Start a new refresh operation
            refreshTokenPromise = authContextRef.refreshToken();
            await refreshTokenPromise;
            // Clear the promise after completion
            refreshTokenPromise = null;
          }

          // Get the new token and retry the original request
          const newToken = await getTokenAsync();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear the promise and let the error propagate
          refreshTokenPromise = null;
          console.error('Token refresh failed:', refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);


// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}



function InnerApp() {
  const auth = useAuth()

  // Store auth reference for axios interceptors
  authContextRef = auth

  return <RouterProvider router={router} context={{ auth }} />
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Analytics />
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
    </QueryClientProvider>
  )
}

const rootElement = document.getElementById('app')!

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
